import * as React from "react"
import { ArrowRight, Loader2, ShieldCheck } from "lucide-react"
import { Button } from "@/components/ui/button"
import { Card, CardContent } from "@/components/ui/card"
import { useBookingT } from "@/hooks/use-translations"
import { claimOAuthInstall, startOAuth, type HrisConnection } from "@/features/integrations/hris/hrisApi"

type Props = {
  provider: string
  providerName: string
  onConnected: (connection: HrisConnection) => void
  onCancel?: () => void
}

/** Takes ?install= / ?install_error= off the address once read, so a reload does not repeat it. */
function takeInstallParams(): { install: string | null; failed: boolean } {
  const url = new URL(window.location.href)
  const install = url.searchParams.get("install")
  const failed = url.searchParams.has("install_error")
  if (install !== null || failed) {
    url.searchParams.delete("install")
    url.searchParams.delete("install_error")
    window.history.replaceState(window.history.state, "", url.pathname + url.search + url.hash)
  }
  return { install, failed }
}

/**
 * HR systems connected by approving the Travel Code app in them (HiBob): we
 * send an admin there, they approve, the HR system sends them back here with
 * ?install=, and the install is attached to this account.
 */
export function HrisOAuthConnectCard({ provider, providerName, onConnected, onCancel }: Props) {
  const t = useBookingT()
  const fill = (text: string) => text.split("{name}").join(providerName)
  const [busy, setBusy] = React.useState(false)
  const [error, setError] = React.useState<string | null>(null)
  const claimed = React.useRef(false)

  React.useEffect(() => {
    if (claimed.current) return
    claimed.current = true
    const { install, failed } = takeInstallParams()
    if (failed) {
      setError(fill(t("hris_oauth_failed", "{name} did not confirm the connection. Try again.")))
      return
    }
    if (install === null) return
    setBusy(true)
    claimOAuthInstall(provider, install)
      .then(onConnected)
      .catch((e) => setError((e as Error).message))
      .finally(() => setBusy(false))
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [provider])

  const go = async () => {
    setError(null)
    setBusy(true)
    try {
      window.location.assign((await startOAuth(provider)).installUrl)
    } catch (e) {
      setError((e as Error).message)
      setBusy(false)
    }
  }

  return (
    <Card>
      <CardContent className="space-y-5 p-6">
        <div className="space-y-1">
          <h2 className="text-lg font-semibold text-neutral-900">{fill(t("hris_connect_title", "Connect {name}"))}</h2>
          <p className="max-w-2xl text-sm text-neutral-800">
            {t(
              "hris_connect_intro",
              "Employees, companies, departments and the traveller data airlines require are kept in sync from your HR system. Nothing changes in Travel Code until you have reviewed a preview and switched the sync on.",
            )}
          </p>
        </div>

        <ol className="max-w-2xl list-decimal space-y-1 pl-5 text-sm text-neutral-800">
          <li>{fill(t("hris_oauth_step_go", "You go to {name} and sign in as a {name} admin."))}</li>
          <li>{fill(t("hris_oauth_step_approve", "{name} shows what Travel Code may read. Approve it."))}</li>
          <li>{fill(t("hris_oauth_step_back", "{name} brings you back here, and you review the employees before anything changes."))}</li>
        </ol>

        {error && <p className="text-sm text-error-700">{error}</p>}

        <div className="flex items-center justify-between gap-3 max-md:flex-col max-md:items-stretch">
          <p className="flex items-center gap-1.5 text-sm text-neutral-800">
            <ShieldCheck className="size-4 text-success-600" />
            {fill(t("hris_oauth_safety", "Travel Code only reads from {name}. You can disconnect it in {name} at any time."))}
          </p>
          <div className="flex gap-2 max-md:flex-col">
            {onCancel && (
              <Button type="button" variant="outline" onClick={onCancel}>
                {t("hris_cancel", "Cancel")}
              </Button>
            )}
            <Button onClick={() => void go()} disabled={busy}>
              {busy ? <Loader2 className="size-4 animate-spin" /> : <ArrowRight className="size-4" />}
              {fill(t("hris_oauth_go", "Continue to {name}"))}
            </Button>
          </div>
        </div>
      </CardContent>
    </Card>
  )
}
